'use client';

import { useCallback } from 'react';
import { VelocityClient } from '@velocity-exchange/sdk';
import {
	MAIN_POOL_ID,
	getUserKey,
	initializeAndSubscribeToNewUserAccount,
} from '@velocity-exchange/common';

import { notify } from 'src/utils/notifications';
import { sleep } from 'src/utils/dateTime';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useSetupUserAccountStateAndListeners } from '../../user/useFetchAndSubscribeSubaccounts/useSetupUserAccountStateAndListeners';

interface AccountInitializationParams {
	isReferred: boolean;
	velocityClient: VelocityClient;
	userIdToInit: number;
	toastId: string;
	runInitializationTransaction: () => Promise<{ txSig: string } | undefined>;
	poolId?: number;
	updatePrevious?: boolean;
	customMessages?: {
		successMessage?: string;
		initializingMessage?: string;
	};
	onSuccess?: (
		userIdToInit: number,
		toastId?: string,
		switchAccount?: boolean
	) => Promise<void>;
	/**
	 * Invoked the moment account creation succeeds, BEFORE the trailing
	 * account-subscription settling sleeps. Callers pass a modal close handler
	 * here so the modal closes immediately instead of lingering for ~3s while
	 * the "Not enough SOL" alert re-renders.
	 */
	onAccountCreated?: () => void;
}

interface HandleCreateUserSuccessParams {
	userIdToInit: number;
	toastId?: string;
	switchAccount?: boolean;
	customMessages?: {
		successMessage?: string;
	};
}

export const useAccountInitialization = () => {
	const velocityClient = useVelocityClient();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const setStore = useVelocityStore((s) => s.set);
	const setAccountsState = useVelocityAccountsStore((s) => s.set);
	const { setupUserAccountStateAndListeners } =
		useSetupUserAccountStateAndListeners();

	const handleCreateVelocityUserSuccess = useCallback(
		async (params: HandleCreateUserSuccessParams) => {
			const { userIdToInit, toastId, switchAccount, customMessages } = params;

			if (!walletAdapter?.publicKey) return;

			const userKey = getUserKey(userIdToInit, walletAdapter.publicKey);

			if (switchAccount !== false) {
				setAccountsState((s) => {
					s.currentUserKey = userKey;
				});
			}

			const newUserAccountPubKey =
				await velocityClient?.getUserAccountPublicKey(
					userIdToInit,
					walletAdapter.publicKey
				);

			setupUserAccountStateAndListeners(newUserAccountPubKey, {
				newAccountId: userIdToInit,
			});

			await velocityClient?.userStats?.fetchAccounts();
			const updatedUserStatsAccount = velocityClient?.userStats?.getAccount();
			if (updatedUserStatsAccount) {
				setStore((s) => {
					s.userStatsAccount = updatedUserStatsAccount;
				});
			}

			if (toastId) {
				notify({
					type: 'success',
					message: customMessages?.successMessage || 'New subaccount created.',
					id: toastId,
				});
			}

			setAccountsState((s) => {
				s.userAccountNotInitialized = false;
			});

			window.localStorage?.removeItem('referrerParam');
		},
		[
			walletAdapter,
			setAccountsState,
			setStore,
			velocityClient,
			setupUserAccountStateAndListeners,
		]
	);

	const runAccountInitializationTxAndHandling = useCallback(
		async (params: AccountInitializationParams): Promise<boolean> => {
			const {
				isReferred,
				velocityClient: initVelocityClient,
				userIdToInit,
				toastId,
				runInitializationTransaction,
				poolId,
				updatePrevious = false,
				customMessages,
				onSuccess,
				onAccountCreated,
			} = params;

			if (!walletAdapter?.publicKey) return false;

			let initializationTxSig: string;

			const result = await initializeAndSubscribeToNewUserAccount(
				initVelocityClient,
				userIdToInit,
				walletAdapter.publicKey,
				{
					initializationStep: async () => {
						try {
							const txResult = await runInitializationTransaction();
							if (!txResult) return false;

							initializationTxSig = txResult.txSig;
							return true;
						} catch (e: any) {
							console.error(e);

							TransactionErrorHandler.handleError({
								error: e,
								toastId,
								wallet: walletAdapter,
							});
							return false;
						}
					},
					postInitializationStep: async () => {
						notify({
							type: 'info',
							message:
								customMessages?.initializingMessage || 'Deposit successful',
							description: 'Subscribing to account...',
							lengthMs: 5000,
							id: toastId,
							updatePrevious,
						});

						if (userIdToInit === 0 && isReferred) {
							notify({
								type: 'success',
								message: 'Referral code applied.',
								updatePrevious,
							});
						}

						setStore((s) => {
							s.referrerParam = null;
							s.referrerParamFailed = false;
						});

						return true;
					},
					handleSuccessStep: async () => {
						// If custom success handler is provided, use it; otherwise use default
						if (onSuccess) {
							await onSuccess(
								userIdToInit,
								toastId,
								!(poolId && poolId !== MAIN_POOL_ID)
							);
						} else {
							await handleCreateVelocityUserSuccess({
								userIdToInit,
								toastId,
								switchAccount: !(poolId && poolId !== MAIN_POOL_ID),
								customMessages,
							});
						}

						// Signal success BEFORE the trailing settling sleeps so callers
						// (e.g. modals) can close immediately instead of lingering.
						onAccountCreated?.();

						await sleep(3000);

						if (initializationTxSig) {
							setAccountsState((s) => {
								s.userAccountNotInitialized = false;
							});
						}

						return true;
					},
				}
			);

			switch (result) {
				case 'ok':
					return true;
				case 'failed_awaitAccountInitializationChainState':
					notify({
						type: 'error',
						description:
							'The operation was successful but unable to subscribe to your new account. Please try refreshing the page.',
						lengthMs: 5000,
					});
					return false;
				case 'failed_handleSuccessStep':
				case 'failed_initializationStep':
				case 'failed_postInitializationStep':
					throw new Error(`Unhandled initialization failure : ${result}`);
			}
		},
		[walletAdapter, setStore, setAccountsState, handleCreateVelocityUserSuccess]
	);

	return {
		runAccountInitializationTxAndHandling,
		handleCreateVelocityUserSuccess,
	};
};
